import { FormField, FormItem, FormLabel } from "@/components/ui/form";
import { TextField, SelectField, DateField, RadioGroup } from "@/components/form";
import { formatPhone, formatEin, formatSsn } from "@/utils/format";
import { StepHeading, useZipAutoFill, OWNERSHIP_TYPES, isSoleProprietor } from "@/features/onboarding/components/wizard";
import { STATE_OPTIONS } from "@/utils/states";
import { optionLabel, selectCopy, useT } from "@/features/onboarding/i18n";
import { type StepProps } from "./schema";

export function LegalStep({ form }: StepProps) {
  const t = useT();

  useZipAutoFill(form.watch("zip"), (st, ci) => {
    const sv = { shouldValidate: form.formState.isSubmitted };
    if (st) form.setValue("state", st, sv);
    if (ci) form.setValue("city", ci, sv);
  });
  useZipAutoFill(form.watch("dbaZip"), (st, ci) => {
    const sv = { shouldValidate: form.formState.isSubmitted };
    if (st) form.setValue("dbaState", st, sv);
    if (ci) form.setValue("dbaCity", ci, sv);
  });
  const ownershipType = form.watch("ownershipType");
  const dbaSameAsLegal = form.watch("dbaSameAsLegal");
  const c = form.control;

  return (
    <div className="space-y-5">
      <StepHeading title={t("legal.title")} subtitle={t("legal.subtitle")} />

      <TextField control={c} name="legalName" label={t("legal.legalName")} required placeholder="Acme Coffee Co." />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField control={c} name="email" label={t("legal.email")} required inputMode="email" placeholder="you@business.com" />
        <TextField control={c} name="phone" label={t("legal.phone")} required type="tel" inputMode="tel" maxLength={14} placeholder="(555) 123-4567" format={formatPhone} />
      </div>

      <TextField control={c} name="website" label={t("legal.website")} type="url" inputMode="url" placeholder="https://" hint={t("common.optional")} />

      <TextField control={c} name="addressLine1" label={t("legal.street")} required placeholder="123 Main St" />

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <TextField control={c} name="zip" label={t("legal.zip")} required inputMode="numeric" maxLength={5} placeholder="94103" format={(v) => v.replace(/\D/g, "").slice(0, 5)} />
        <SelectField control={c} name="state" label={t("legal.state")} required {...selectCopy(t, t("legal.state"))} options={STATE_OPTIONS} />
        <TextField control={c} name="city" label={t("legal.city")} required placeholder="San Francisco" className="col-span-2 sm:col-span-1" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SelectField control={c} name="ownershipType" label={t("legal.ownershipType")} required {...selectCopy(t, t("legal.ownershipType"))} options={OWNERSHIP_TYPES.map((o) => [o, optionLabel(t, "own", o)])} />
        <DateField control={c} name="businessStartDate" label={t("legal.startDate")} required />
      </div>

      {isSoleProprietor(ownershipType) ? (
        <TextField control={c} name="ssn" label={t("legal.ssn")} required inputMode="numeric" maxLength={11} placeholder="XXX-XX-XXXX" format={formatSsn} hint={t("legal.ssnHint")} />
      ) : (
        <TextField control={c} name="ein" label={t("legal.ein")} required inputMode="numeric" maxLength={10} placeholder="XX-XXXXXXX" format={formatEin} hint={t("legal.einHint")} />
      )}

      <div className="pt-4 border-t border-slate-200">
        <StepHeading title={t("legal.dbaTitle")}
          subtitle={t("legal.dbaSubtitle")} />

        <FormField
          control={c}
          name="dbaSameAsLegal"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("legal.dbaSame")}</FormLabel>
              <RadioGroup
                value={field.value ? "yes" : "no"}
                onChange={(v) => field.onChange(v === "yes")}
                options={[
                  { value: "yes", label: t("legal.dbaYes") },
                  { value: "no", label: t("legal.dbaNo") },
                ]}
              />
            </FormItem>
          )}
        />

        {!dbaSameAsLegal && (
          <div className="space-y-4 mt-4">
            <TextField control={c} name="dbaName" label={t("legal.dbaName")} required placeholder={t("legal.dbaNamePlaceholder")} />
            <TextField control={c} name="dbaAddressLine1" label={t("legal.street")} placeholder="123 Main St" />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <TextField control={c} name="dbaZip" label={t("legal.zip")} inputMode="numeric" maxLength={5} format={(v) => v.replace(/\D/g, "").slice(0, 5)} />
              <SelectField control={c} name="dbaState" label={t("legal.state")} {...selectCopy(t, t("legal.state"))} options={STATE_OPTIONS} />
              <TextField control={c} name="dbaCity" label={t("legal.city")} className="col-span-2 sm:col-span-1" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
